import {
  INVALID_BUDGET_MESSAGE,
  MAX_BUDGET_VALUE,
  MIN_BUDGET_VALUE,
} from "components/common/CreateProjectSheet/CreateProjectSheet.constants";
import { toFormattedCurrency } from "utils/currencyUtil";
import * as yup from "yup";

interface BudgetLocation {
  name?: string;
  place_id?: string | null;
}

export type { BudgetLocation };

export interface BudgetAssumptions {
  max_budget: MaxBudgetDto;
  prep: ValueUnitDto;
  shoot: ValueUnitDto | null;
  post: ValueUnitDto;
  wrap: ValueUnitDto;
  use_stripboard_locations: boolean;
  use_stripboard_shoot_time: boolean;
  location?: BudgetLocation;
  guilds?: GuildsDto;
}

export enum GuildType {
  UNION = "union",
  NON_UNION = "non-union",
}

interface GuildsDto {
  dga: boolean;
  wga: boolean;
  sag: boolean;
  iatse: boolean;
  teamsters: boolean;
}

interface MaxBudgetDto {
  value: number;
  currency: Currency;
}

export enum Currency {
  USD = "USD",
  EUR = "EUR",
  AUD = "AUD",
  CAD = "CAD",
  NZD = "NZD",
}

export enum TimeUnit {
  WEEK = "W",
  DAY = "D",
}

export const TimeUnitLabels: { [key in TimeUnit]: string } = {
  [TimeUnit.WEEK]: "Weeks",
  [TimeUnit.DAY]: "Days",
};

interface ValueUnitDto {
  value: number;
  unit: TimeUnit;
}

const MAX_WEEKS = 16;
const MAX_DAYS = MAX_WEEKS * 7;

const getLowerThanLockedAccountsBudgetValidationErrorMessage = (lockedAccountsBudget: number) =>
  `Max Budget should be greater than sum of locked accounts - ${toFormattedCurrency(
    lockedAccountsBudget
  )}`;

export const createBudgetAssumptionsSchema = (lockedAccountsBudget: number) =>
  yup.object().shape({
    max_budget: yup.object({
      value: yup
        .string()
        .required("Max Budget is required")
        .test(
          "max_budget",
          INVALID_BUDGET_MESSAGE,
          (value) => parseInt(value.replace(/,/g, ""), 10) >= MIN_BUDGET_VALUE
        )
        .test(
          "max_budget",
          getLowerThanLockedAccountsBudgetValidationErrorMessage(lockedAccountsBudget),
          (value) => parseInt(value.replace(/,/g, ""), 10) > lockedAccountsBudget
        )
        .test(
          "max_budget",
          INVALID_BUDGET_MESSAGE,
          (value) => parseInt(value.replace(/,/g, ""), 10) <= MAX_BUDGET_VALUE
        ),
      currency: yup.mixed<Currency.USD>().oneOf([Currency.USD]).required(),
    }),
    prep: yup.object({
      value: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required("Value  is required")
        .min(0, "Value should be at least 0")
        .when("unit", (unit: any, schema: any) => {
          return unit == TimeUnit.WEEK
            ? schema.max(MAX_WEEKS, `Value should not exceed  ${MAX_WEEKS}`)
            : schema.max(MAX_DAYS, `Value should not exceed  ${MAX_DAYS}`);
        }),
      unit: yup.mixed<TimeUnit>().oneOf(Object.values(TimeUnit)).required(),
    }),
    shoot: yup
      .object({
        value: yup
          .number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .required("Value is required")
          .min(1, "Value should be at least 1")
          .when("unit", (unit: any, schema: any) => {
            return unit == TimeUnit.WEEK
              ? schema.max(MAX_WEEKS, `Value should not exceed  ${MAX_WEEKS}`)
              : schema.max(MAX_DAYS, `Value should not exceed  ${MAX_DAYS}`);
          }),
        unit: yup.mixed<TimeUnit>().oneOf(Object.values(TimeUnit)).required(),
      })
      .nullable()
      .test(
        "is-required-if-use-stripboard-shoot-time-is-false",
        "Value is required",
        function (value) {
          if (this.parent.use_stripboard_shoot_time !== false || value != null) return true;
          // Report on shoot.value, the field the dialog renders an error for.
          return this.createError({ path: `${this.path}.value`, message: "Value is required" });
        }
      ),
    post: yup.object({
      value: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required("Value is required")
        .min(0, "Value should be at least 0")
        .when("unit", (unit: any, schema: any) => {
          return unit == TimeUnit.WEEK
            ? schema.max(MAX_WEEKS, `Value should not exceed  ${MAX_WEEKS}`)
            : schema.max(MAX_DAYS, `Value should not exceed  ${MAX_DAYS}`);
        }),
      unit: yup.mixed<TimeUnit>().oneOf(Object.values(TimeUnit)).required(),
    }),
    wrap: yup.object({
      value: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required("Value is required")
        .min(0, "Value should be at least 0")
        .max(16, "Value should not exceed 16")
        .when("unit", (unit: any, schema: any) => {
          return unit == TimeUnit.WEEK
            ? schema.max(MAX_WEEKS, `Value should not exceed  ${MAX_WEEKS}`)
            : schema.max(MAX_DAYS, `Value should not exceed  ${MAX_DAYS}`);
        }),
      unit: yup.mixed<TimeUnit>().oneOf(Object.values(TimeUnit)).required(),
    }),
    use_stripboard_locations: yup.boolean().required(),
    use_stripboard_shoot_time: yup.boolean().required(),
    location: yup
      .object({
        name: yup.string().max(255, "Location should be less than 255 characters"),
        place_id: yup.string().nullable(),
      })
      .optional()
      .test(
        "is-required-if-use-stripboard-locations-is-false",
        "Value is required.",
        function (value) {
          if (this.parent.use_stripboard_locations === false && !value?.name) {
            return false;
          }
          return true;
        }
      ),
  });
